import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';

const STORAGE_BUCKET = 'property-images';

function fileExtension(file) {
  const name = file.name || '';
  const dot = name.lastIndexOf('.');
  if (dot >= 0 && name.length - dot <= 6) return name.slice(dot).toLowerCase();
  if (file.type === 'image/png') return '.png';
  if (file.type === 'image/webp') return '.webp';
  if (file.type === 'image/gif') return '.gif';
  if (file.type === 'video/mp4') return '.mp4';
  if (file.type === 'video/webm') return '.webm';
  if (file.type === 'video/quicktime') return '.mov';
  return '.jpg';
}

function kindFromUrl(url) {
  if (!url) return 'image';
  return /\.(mp4|webm|mov)(\?|$)/i.test(url) ? 'video' : 'image';
}

async function uploadMaintenanceMedia(supabaseClient, requestId, file, basename) {
  const path = `maintenance/${requestId}/${basename}-${Date.now()}${fileExtension(file)}`;
  const { error } = await supabaseClient.storage
    .from(STORAGE_BUCKET)
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;
  const { data } = supabaseClient.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

function MaintenanceNew() {
  const navigate = useNavigate();
  const { id: editRequestId } = useParams();
  const isEdit = Boolean(editRequestId);

  const mediaInputRef = React.useRef(null);

  const [propertyOptions, setPropertyOptions] = React.useState([]);
  const [propertiesLoading, setPropertiesLoading] = React.useState(true);
  const [propertiesError, setPropertiesError] = React.useState('');

  const [tenantName, setTenantName] = React.useState('');
  const [contactInfo, setContactInfo] = React.useState('');
  const [propertyId, setPropertyId] = React.useState('');
  const [roomLabel, setRoomLabel] = React.useState('');
  const [issue, setIssue] = React.useState('');
  const [status, setStatus] = React.useState('pending');
  const [assignee, setAssignee] = React.useState('');

  const [mediaItems, setMediaItems] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [loadInitial, setLoadInitial] = React.useState(() => Boolean(editRequestId));
  const [loadError, setLoadError] = React.useState('');

  const mediaItemsRef = React.useRef(mediaItems);
  mediaItemsRef.current = mediaItems;

  React.useEffect(() => {
    return () => {
      mediaItemsRef.current.forEach((item) => {
        if (item.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
      });
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!supabase) {
        setPropertiesError(
          'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
        );
        setPropertyOptions([]);
        setPropertiesLoading(false);
        return;
      }
      setPropertiesLoading(true);
      setPropertiesError('');
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, location')
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        setPropertiesError(error.message);
        setPropertyOptions([]);
      } else {
        setPropertyOptions(data || []);
      }
      setPropertiesLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!isEdit || !editRequestId) {
      setLoadInitial(false);
      setLoadError('');
      return undefined;
    }

    if (!supabase) {
      setLoadInitial(false);
      setLoadError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadInitial(true);
      setLoadError('');

      const { data: row, error: rowErr } = await supabase
        .from('maintenance_requests')
        .select('*')
        .eq('id', editRequestId)
        .single();

      if (cancelled) return;
      if (rowErr) {
        setLoadError(rowErr.message);
        setLoadInitial(false);
        return;
      }

      setTenantName(row.tenant_name || '');
      setContactInfo(row.contact_info || '');
      setPropertyId(row.property_id || '');
      setRoomLabel(row.room_label || '');
      setIssue(row.issue_description || '');
      setStatus(row.status || 'pending');
      setAssignee(row.assignee || '');

      const { data: mediaRows } = await supabase
        .from('maintenance_request_media')
        .select('id, file_url')
        .eq('request_id', editRequestId)
        .order('sort_order', { ascending: true });

      if (cancelled) return;

      setMediaItems(
        (mediaRows || []).map((m) => ({
          id: `db-m-${m.id}`,
          file: null,
          url: m.file_url,
          kind: kindFromUrl(m.file_url),
        }))
      );

      setLoadInitial(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editRequestId]);

  const onMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const next = files
      .map((file) => {
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        if (!isVideo && !isImage) return null;
        return {
          id: `${Date.now()}-${file.name}-${Math.random().toString(36).slice(2, 9)}`,
          file,
          url: URL.createObjectURL(file),
          kind: isVideo ? 'video' : 'image',
        };
      })
      .filter(Boolean);

    if (next.length) {
      setMediaItems((prev) => [...prev, ...next]);
    }
    e.target.value = '';
  };

  const removeMedia = (id) => {
    setMediaItems((prev) => {
      const item = prev.find((m) => m.id === id);
      if (item?.url && item.url.startsWith('blob:')) URL.revokeObjectURL(item.url);
      return prev.filter((m) => m.id !== id);
    });
  };

  const selectedPropertyName = () => {
    if (!propertyId) return '';
    const p = propertyOptions.find((x) => x.id === propertyId);
    if (!p) return '';
    return p.location ? `${p.name} (${p.location})` : p.name;
  };

  const deleteRequestIfAny = async (requestId) => {
    if (!requestId || !supabase) return;
    await supabase.from('maintenance_requests').delete().eq('id', requestId);
  };

  const syncMediaRows = async (requestId) => {
    await supabase.from('maintenance_request_media').delete().eq('request_id', requestId);
    for (let i = 0; i < mediaItems.length; i += 1) {
      const item = mediaItems[i];
      const url = item.file
        ? await uploadMaintenanceMedia(
            supabase,
            requestId,
            item.file,
            `media-${i}`
          )
        : item.url;
      if (!url || url.startsWith('blob:')) continue;
      const { error: mediaErr } = await supabase
        .from('maintenance_request_media')
        .insert({
          request_id: requestId,
          file_url: url,
          sort_order: i,
        });
      if (mediaErr) throw mediaErr;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');

    if (!supabase) {
      setSaveError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return;
    }

    const issueTrim = issue.trim();
    if (!issueTrim) {
      setSaveError('Please describe the issue.');
      return;
    }

    setSaving(true);
    let requestId = null;
    let created = false;

    try {
      const payload = {
        tenant_name: tenantName.trim(),
        contact_info: contactInfo.trim(),
        property_id: propertyId || null,
        property_name: selectedPropertyName(),
        room_label: roomLabel.trim(),
        issue_description: issueTrim,
        status,
        assignee: assignee.trim(),
      };

      if (isEdit) {
        requestId = editRequestId;
        const { error: upErr } = await supabase
          .from('maintenance_requests')
          .update(payload)
          .eq('id', requestId);
        if (upErr) throw upErr;
        await syncMediaRows(requestId);
      } else {
        const { data: row, error: insertErr } = await supabase
          .from('maintenance_requests')
          .insert(payload)
          .select('id')
          .single();

        if (insertErr) throw insertErr;
        requestId = row.id;
        created = true;
        await syncMediaRows(requestId);
      }

      navigate('/maintenance');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      if (created) await deleteRequestIfAny(requestId);
      setSaveError(
        err?.message ||
          err?.error_description ||
          'Could not save request. Run sql/add_maintenance_supabase.sql and ensure the property-images storage bucket exists.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="maint-root">
      <UniversalHeader />

      <div className="maint-shell">
        <header className="maint-header">
          <div>
            <h1 className="maint-title">
              {isEdit ? 'Edit maintenance request' : 'New maintenance request'}
            </h1>
            <p className="maint-subtitle">
              {isEdit
                ? 'Update the issue, property, status, and media, then save.'
                : 'Log an issue, choose the property, and assign it to your team.'}
            </p>
          </div>

          <div className="maint-actions">
            <button
              type="button"
              className="btn-ghost maint-btn"
              disabled={saving || loadInitial}
              onClick={() => navigate('/maintenance')}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="maint-new-form"
              className="btn-primary maint-btn"
              disabled={saving || propertiesLoading || loadInitial}
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save request'}
            </button>
          </div>
        </header>

        {saveError ? (
          <p className="properties-save-error" role="alert">
            {saveError}
          </p>
        ) : null}
        {propertiesError ? (
          <p className="properties-save-error" role="alert">
            {propertiesError}
          </p>
        ) : null}
        {loadError ? (
          <p className="properties-save-error" role="alert">
            {loadError}{' '}
            <button
              type="button"
              className="btn-ghost maint-btn"
              style={{ marginTop: 8 }}
              onClick={() => navigate('/maintenance')}
            >
              Back to maintenance
            </button>
          </p>
        ) : null}

        <main className="maint-layout">
          <section className="maint-list">
            <div className="properties-detail-card">
              {isEdit && loadInitial ? (
                <p className="properties-list-empty" style={{ margin: '20px 0' }}>
                  Loading request…
                </p>
              ) : null}
              <form
                id="maint-new-form"
                className="properties-add-form"
                onSubmit={handleSubmit}
                style={{
                  display: isEdit && (loadInitial || loadError) ? 'none' : undefined,
                }}
              >
                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="req-tenant">
                      Tenant name
                    </label>
                    <div className="input-shell">
                      <input
                        id="req-tenant"
                        className="input-element"
                        type="text"
                        placeholder="e.g. Aisha Khan"
                        value={tenantName}
                        onChange={(e) => setTenantName(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="req-contact">
                      Contact info
                    </label>
                    <div className="input-shell">
                      <input
                        id="req-contact"
                        className="input-element"
                        type="text"
                        placeholder="Email or phone"
                        value={contactInfo}
                        onChange={(e) => setContactInfo(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="req-property">
                      Property
                    </label>
                    <div className="input-shell">
                      <select
                        id="req-property"
                        className="input-element"
                        value={propertyId}
                        onChange={(e) => setPropertyId(e.target.value)}
                        disabled={propertiesLoading || !supabase}
                      >
                        <option value="">
                          {propertiesLoading
                            ? 'Loading properties…'
                            : 'Select a property…'}
                        </option>
                        {propertyOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                            {p.location ? ` — ${p.location}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {!propertiesLoading &&
                    supabase &&
                    propertyOptions.length === 0 ? (
                      <p className="tenant-property-hint">
                        No properties yet. Add one under Properties first.
                      </p>
                    ) : null}
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="req-room">
                      Room
                    </label>
                    <div className="input-shell">
                      <input
                        id="req-room"
                        className="input-element"
                        type="text"
                        placeholder="e.g. 3B"
                        value={roomLabel}
                        onChange={(e) => setRoomLabel(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="field-wrapper">
                  <label className="field-label" htmlFor="req-issue">
                    Issue description
                  </label>
                  <div className="input-shell">
                    <input
                      id="req-issue"
                      className="input-element"
                      type="text"
                      placeholder="Describe the problem in 1–2 sentences"
                      value={issue}
                      onChange={(e) => setIssue(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="req-status">
                      Status
                    </label>
                    <div className="input-shell">
                      <select
                        id="req-status"
                        className="input-element"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="req-assignee">
                      Assign to
                    </label>
                    <div className="input-shell">
                      <input
                        id="req-assignee"
                        className="input-element"
                        type="text"
                        placeholder="e.g. Alex (Plumbing)"
                        value={assignee}
                        onChange={(e) => setAssignee(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="properties-media">
                  <div className="properties-media-header">
                    <span>Photos / videos</span>
                    <span className="properties-media-hint">
                      Add images or short clips to document the issue (JPG, PNG, WebP, MP4,
                      WebM).
                    </span>
                  </div>
                  <div className="properties-media-block">
                    <input
                      ref={mediaInputRef}
                      id="req-maintenance-media"
                      className="properties-media-input"
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={onMediaChange}
                    />
                    <div className="properties-media-gallery-toolbar">
                      <button
                        type="button"
                        className="btn-ghost properties-room-add"
                        onClick={() => mediaInputRef.current?.click()}
                      >
                        + Upload media
                      </button>
                      {mediaItems.length > 0 ? (
                        <span className="properties-media-count">
                          {mediaItems.length} file{mediaItems.length === 1 ? '' : 's'}
                        </span>
                      ) : null}
                    </div>
                    {mediaItems.length > 0 ? (
                      <ul
                        className="properties-media-gallery-grid maint-media-grid"
                        aria-label="Uploaded media previews"
                      >
                        {mediaItems.map((item, index) => (
                          <li key={item.id} className="properties-media-gallery-item">
                            {item.kind === 'video' ? (
                              <video
                                className="maint-media-video"
                                src={item.url}
                                controls
                                muted
                                playsInline
                                aria-label={`Video preview ${index + 1}`}
                              />
                            ) : (
                              <img
                                src={item.url}
                                alt={`Upload preview ${index + 1}`}
                              />
                            )}
                            <button
                              type="button"
                              className="properties-media-gallery-remove"
                              aria-label="Remove file"
                              onClick={() => removeMedia(item.id)}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default MaintenanceNew;
